import { ANALYTICS_TRIGGERS } from '~/utils/analyticsTriggers'
import { applicationIntent } from '~/utils/applicationIntent'
import { EMPTY_OVERVIEW_ENGAGEMENT } from '~/utils/modalEngagement'

/**
 * Analytics utility functions for PostHog tracking
 * Use these methods throughout your application for consistent event tracking
 */

// Tracks which organization IDs have already fired first_candidate_viewed
// this session, matching the single-occurrence semantics of the funnel step.
const trackedFirstCandidateViewedOrgIds = new Set()

// Test-only escape hatch — call in beforeEach to prevent cross-test coupling.
export const _resetTrackedFirstCandidateViewedOrgIds = () =>
  trackedFirstCandidateViewedOrgIds.clear()

export const analytics = {
  COMPANY_GROUP_TYPE: 'company',

  getPosthogClient() {
    if (!process.client || typeof window === 'undefined') {
      return null
    }

    return window?.$nuxt?.$posthog || null
  },

  // Track custom events. Best-effort: a throwing PostHog client must never
  // break product flows that call analytics before navigation or submits.
  track(eventName, properties = {}) {
    const posthog = this.getPosthogClient()
    if (typeof posthog?.capture !== 'function') {
      return
    }

    try {
      posthog.capture(eventName, properties)
    } catch (_error) {
      // Intentionally swallow — analytics must not block user journeys.
    }
  },

  // Track job application events
  trackJobApplication(jobId, jobTitle, companyName) {
    this.track('job_application_submitted', {
      job_id: jobId,
      job_title: jobTitle,
      company_name: companyName,
      timestamp: new Date().toISOString()
    })
  },

  // Track job search events
  trackJobSearch(searchQuery, filters = {}) {
    this.track('job_search_performed', {
      search_query: searchQuery,
      filters: filters,
      timestamp: new Date().toISOString()
    })
  },

  // Track profile completion events
  trackProfileCompletion(section, completionPercentage) {
    this.track('profile_section_completed', {
      section: section,
      completion_percentage: completionPercentage,
      timestamp: new Date().toISOString()
    })
  },

  // Track personality quiz events
  trackPersonalityQuiz(action, quizData = {}) {
    this.track(`personality_quiz_${action}`, {
      ...quizData,
      timestamp: new Date().toISOString()
    })
  },

  // Track organization events
  trackOrganizationAction(action, organizationData = {}) {
    this.track(`organization_${action}`, {
      ...organizationData,
      timestamp: new Date().toISOString()
    })
  },

  // Track match-related events
  trackMatchEvent(action, matchData = {}) {
    this.track(`match_${action}`, {
      ...matchData,
      timestamp: new Date().toISOString()
    })
  },

  // Track UTM source conversions
  trackUtmConversion(utmSource, conversionType) {
    this.track('utm_conversion', {
      utm_source: utmSource,
      conversion_type: conversionType, // 'registration', 'job_application', etc.
      timestamp: new Date().toISOString()
    })
  },

  // Set user properties
  setUserProperties(properties) {
    const posthog = this.getPosthogClient()
    if (typeof posthog?.people?.set !== 'function') {
      return
    }

    posthog.people.set(properties)
  },

  toStringOrNull(value) {
    if (value === null || value === undefined || value === '') {
      return null
    }

    return String(value)
  },

  getCandidateIdentity({
    userId = null,
    applicantProfileId = null,
    userType = 'candidate'
  } = {}) {
    return {
      user_id: this.toStringOrNull(userId),
      applicant_profile_id: this.toStringOrNull(applicantProfileId),
      user_type: userType
    }
  },

  buildCompanyActivationProperties({
    organizationId,
    userId = null,
    jobOfferId = null,
    applicationId = null,
    applicantProfileId = null,
    sourceSurface = null,
    userType = 'employer',
    extra = {}
  } = {}) {
    if (!organizationId) return null
    const normalizedOrganizationId = this.toStringOrNull(organizationId)

    return {
      organization_id: normalizedOrganizationId,
      user_id: this.toStringOrNull(userId),
      job_offer_id: this.toStringOrNull(jobOfferId),
      application_id: this.toStringOrNull(applicationId),
      applicant_profile_id: this.toStringOrNull(applicantProfileId),
      source_surface: sourceSurface || null,
      user_type: userType,
      $groups: {
        [this.COMPANY_GROUP_TYPE]: normalizedOrganizationId
      },
      ...extra,
    }
  },

  trackCompanyActivationEvent(eventName, payload = {}) {
    const properties = this.buildCompanyActivationProperties(payload)
    if (!properties) return

    this.track(eventName, properties)
  },

  trackJobCreationStarted(payload = {}) {
    this.trackCompanyActivationEvent('job_creation_started', payload)
  },

  trackPersonalityProfileDefined(payload = {}) {
    this.trackCompanyActivationEvent('personality_profile_defined', payload)
  },

  trackJobPublished(payload = {}) {
    this.trackCompanyActivationEvent('job_published', payload)
  },

  // Fires when a hiring manager opens the team-assessment invite window.
  // source_surface distinguishes the results page from the job work-style roster.
  trackTeamAssessmentInviteClicked(payload = {}) {
    this.trackCompanyActivationEvent(
      'team_assessment_invite_clicked',
      payload
    )
  },

  trackFirstCandidateViewed(payload = {}) {
    const orgId = String(payload.organizationId ?? '')
    if (!orgId || trackedFirstCandidateViewedOrgIds.has(orgId)) {
      return
    }
    trackedFirstCandidateViewedOrgIds.add(orgId)
    this.trackCompanyActivationEvent('first_candidate_viewed', payload)
  },

  // ATS candidate behavior instrumentation — APR-1256.
  // Pulls the three score signals out of an application's `match` object
  // (same shape on list rows and the modal's matchData prop). Null when
  // absent so "no data" is distinguishable from a real 0.
  extractCandidateScores(match) {
    const m = match || {}
    return {
      work_style_score: m?.percents?.personality ?? null,
      requirements_score: m?.details?.hardskillAverage ?? null,
      match_score: m?.match ?? null
    }
  },

  // Maps an application target status to the product-facing decision verb
  // used in candidate_decision_made.
  mapStatusToDecision(status) {
    switch (status) {
      case 'accepted':
        return 'shortlist'
      case 'interview':
        return 'interview'
      case 'hired':
        return 'hire'
      case 'denied':
        return 'decline'
      // Unmapped statuses surface as null rather than leaking a raw status into
      // the decision dimension, so an unhandled new status is visible at a glance.
      default:
        return null
    }
  },

  // Shared list/score/identity context for both ATS instrumentation events.
  buildAtsCandidateContext({
    organizationId = null,
    userId = null,
    jobOfferId = null,
    applicationId = null,
    applicantProfileId = null,
    stage = null,
    isTopFit = false,
    listSortActive = null,
    listPosition = null,
    match = null
  } = {}) {
    const normalizedOrganizationId = this.toStringOrNull(organizationId)
    const context = {
      organization_id: normalizedOrganizationId,
      user_id: this.toStringOrNull(userId),
      job_offer_id: this.toStringOrNull(jobOfferId),
      application_id: this.toStringOrNull(applicationId),
      applicant_profile_id: this.toStringOrNull(applicantProfileId),
      stage: stage || null,
      is_top_fit: Boolean(isTopFit),
      list_sort_active: listSortActive || null,
      list_position: typeof listPosition === 'number' ? listPosition : null,
      ...this.extractCandidateScores(match)
    }

    if (normalizedOrganizationId) {
      context.$groups = {
        [this.COMPANY_GROUP_TYPE]: normalizedOrganizationId
      }
    }

    return context
  },

  trackCandidateProfileOpened({ sourceSurface = null, ...context } = {}) {
    this.track('candidate_profile_opened', {
      ...this.buildAtsCandidateContext(context),
      source_surface: sourceSurface || null,
      timestamp: new Date().toISOString()
    })
  },

  trackCandidateDecisionMade({
    origin = 'ats_view',
    decision = null,
    isBulkAction = false,
    sourceSurface = null,
    modal = null,
    ...context
  } = {}) {
    // ATS-view decisions carry the modal fields as empty/zero defaults so the
    // event shape is identical regardless of origin.
    const modalDefaults = {
      active_tab_at_decision: null,
      tabs_visited: [],
      work_style_tab_viewed: false,
      requirements_tab_viewed: false,
      attachments_tab_viewed: false,
      time_on_work_style_ms: 0,
      time_on_requirements_ms: 0,
      time_on_attachments_ms: 0,
      time_in_modal_ms: 0,
      last_non_work_style_tab_visited: null,
      // AI verdict accordion engagement (modal visits only).
      ai_verdict_insight_expanded: false,
      ai_verdict_sections_opened: [],
      ai_verdict_insights_opened_count: 0,
      ...EMPTY_OVERVIEW_ENGAGEMENT
    }

    this.track('candidate_decision_made', {
      ...this.buildAtsCandidateContext(context),
      source_surface: sourceSurface || null,
      origin,
      decision,
      is_bulk_action: Boolean(isBulkAction),
      ...modalDefaults,
      ...(modal || {}),
      timestamp: new Date().toISOString()
    })
  },

  trackPersonalityOverviewEngaged({
    sourceSurface = null,
    origin = 'modal',
    overview = null,
    ...context
  } = {}) {
    this.track('personality_overview_engaged', {
      ...this.buildAtsCandidateContext(context),
      source_surface: sourceSurface || null,
      origin,
      ...EMPTY_OVERVIEW_ENGAGEMENT,
      ...(overview || {}),
      timestamp: new Date().toISOString()
    })
  },

  detectPathType({ jobId = null, routePath = null } = {}) {
    if (jobId) return 'job_first'

    if (typeof routePath === 'string' && routePath.includes('/job-offer/')) {
      return 'job_first'
    }

    if (process.client) {
      const storedJobOfferId = localStorage.getItem('apropos-jobs__jobOfferId')
      if (storedJobOfferId) return 'job_first'
    }

    return 'profile_first'
  },

  detectSignupMethod({
    provider = null,
    hasInvitationToken = false,
    routeQuery = {}
  } = {}) {
    if (hasInvitationToken || routeQuery?.token || routeQuery?.invitation_token) {
      return 'invite'
    }

    const method = (provider || routeQuery?.provider || routeQuery?.method || '')
      .toString()
      .toLowerCase()

    if (method.includes('linkedin')) return 'linkedin'
    if (method.includes('google')) return 'google'

    return 'email'
  },

  detectReferralSource({ routeQuery = {}, hasJobContext = false } = {}) {
    if (routeQuery?.utm_source) {
      return routeQuery.utm_source
    }

    if (routeQuery?.token || routeQuery?.invitation_token) {
      return 'invite'
    }

    if (hasJobContext) {
      return 'job-board'
    }

    if (process.client && localStorage.getItem('apropos-jobs__jobOfferId')) {
      return 'job-board'
    }

    return 'direct'
  },

  // Applicant Activation Events - APR-981
  trackCandidateSignup({
    userId,
    applicantProfileId = null,
    signupMethod = 'email',
    referralSource = 'direct',
    pathType = 'profile_first',
    jobId = null,
    trigger = ANALYTICS_TRIGGERS.REGISTER_FORM,
    userType = 'candidate'
  } = {}) {
    if (!userId) return
    const identity = this.getCandidateIdentity({
      userId,
      applicantProfileId,
      userType
    })

    this.track('candidate_signup_completed', {
      ...identity,
      signup_method: signupMethod,
      referral_source: referralSource,
      path_type: pathType,
      job_id: jobId ? jobId.toString() : null,
      trigger,
      timestamp: new Date().toISOString()
    })
  },

  // Apply-source attribution: where the applicant opened this job from.
  // An explicit value wins; otherwise fall back to the stamp written by the
  // last in-app card click for this job, else a direct (unattributed) open.
  resolveSourceSurface({ jobId = null, sourceSurface = null } = {}) {
    if (sourceSurface) return sourceSurface
    if (process.client) {
      const stamped = applicationIntent.getApplySource(jobId)
      if (stamped) return stamped
    }
    return 'direct_link'
  },

  trackApplicationStarted({
    jobId,
    pathType = 'profile_first',
    hasCompletedAssessment = false,
    userId = null,
    applicantProfileId = null,
    trigger = ANALYTICS_TRIGGERS.APPLY,
    userType = 'candidate',
    sourceSurface = null
  } = {}) {
    if (!jobId) return
    const identity = this.getCandidateIdentity({
      userId,
      applicantProfileId,
      userType
    })

    // Read-only: the stamp must survive register/setup so the later
    // application_submitted for the same job keeps the same source.
    this.track('application_started', {
      ...identity,
      job_id: jobId.toString(),
      path_type: pathType,
      has_completed_assessment: hasCompletedAssessment,
      trigger,
      source_surface: this.resolveSourceSurface({ jobId, sourceSurface }),
      timestamp: new Date().toISOString()
    })
  },

  trackApplicationSubmitted({
    applicationId,
    jobId,
    matchScore = null,
    pathType = 'profile_first',
    userId = null,
    applicantProfileId = null,
    trigger = ANALYTICS_TRIGGERS.APPLICATION_FORM_SUBMIT,
    userType = 'candidate',
    sourceSurface = null
  } = {}) {
    if (!jobId) return
    const identity = this.getCandidateIdentity({
      userId,
      applicantProfileId,
      userType
    })
    const resolvedSourceSurface = this.resolveSourceSurface({
      jobId,
      sourceSurface
    })

    // Consume the stamp on successful submit so a later re-apply to the
    // same job without a card click is not misattributed.
    if (process.client && applicationIntent.getApplySource(jobId)) {
      applicationIntent.clearApplySource()
    }

    this.track('application_submitted', {
      ...identity,
      application_id: applicationId ? applicationId.toString() : null,
      job_id: jobId.toString(),
      match_score: matchScore,
      path_type: pathType,
      trigger,
      source_surface: resolvedSourceSurface,
      timestamp: new Date().toISOString()
    })
  },

  trackAssessmentStarted({
    jobId = null,
    assessmentType = 'personality',
    trigger = ANALYTICS_TRIGGERS.ONBOARDING,
    pathType = 'profile_first',
    userId = null,
    applicantProfileId = null,
    userType = 'candidate'
  } = {}) {
    const identity = this.getCandidateIdentity({
      userId,
      applicantProfileId,
      userType
    })

    this.track('assessment_started', {
      ...identity,
      job_id: jobId ? jobId.toString() : null,
      assessment_type: assessmentType,
      trigger: trigger,
      path_type: pathType,
      timestamp: new Date().toISOString()
    })
  },

  trackAssessmentCompleted({
    jobId = null,
    assessmentType = 'personality',
    durationSeconds = null,
    trigger = ANALYTICS_TRIGGERS.ONBOARDING,
    pathType = 'profile_first',
    personalityTypes = [],
    questionCount = null,
    userId = null,
    applicantProfileId = null,
    userType = 'candidate'
  } = {}) {
    const identity = this.getCandidateIdentity({
      userId,
      applicantProfileId,
      userType
    })

    this.track('assessment_completed', {
      ...identity,
      job_id: jobId ? jobId.toString() : null,
      assessment_type: assessmentType,
      duration_seconds: durationSeconds,
      trigger: trigger,
      path_type: pathType,
      personality_types: personalityTypes,
      question_count: questionCount,
      timestamp: new Date().toISOString()
    })
  },

  trackAssessmentAbandoned({
    jobId = null,
    assessmentType = 'personality',
    stepReached = null,
    questionsCompleted = null,
    durationSeconds = null,
    trigger = ANALYTICS_TRIGGERS.ONBOARDING,
    abandonmentType = 'navigation',
    userType = 'candidate'
  } = {}) {
    this.track('assessment_abandoned', {
      job_id: jobId ? jobId.toString() : null,
      assessment_type: assessmentType,
      step_reached: stepReached,
      questions_completed: questionsCompleted,
      duration_seconds: durationSeconds,
      trigger: trigger,
      abandonment_type: abandonmentType,
      user_type: userType,
      timestamp: new Date().toISOString()
    })
  },

  // Fires when the setup wizard's criteria step is successfully saved.
  // This is the "setup actually finished" milestone for funnel tracking:
  // earlier than applicant_onboarding_completed (which waits for modal
  // dismiss / post-setup navigation) and independent of whether a job
  // application is submitted. Additive — does not replace either event.
  trackApplicantSetupCompleted({
    applicantProfileId = null,
    pathType = null,
    jobId = null,
    ctaLabel = null,
    userId = null,
    trigger = ANALYTICS_TRIGGERS.ONBOARDING,
    userType = 'candidate'
  } = {}) {
    const identity = this.getCandidateIdentity({
      userId,
      applicantProfileId,
      userType
    })

    this.track('applicant_setup_completed', {
      ...identity,
      path_type: pathType,
      job_id: jobId ? jobId.toString() : null,
      cta_label: ctaLabel,
      trigger,
      timestamp: new Date().toISOString()
    })
  },

  // Fires when an applicant finishes the setup wizard and navigates to a
  // post-onboarding screen (dashboard, applications, or applicant-profile).
  // Used to trigger the applicant onboarding feedback survey. This is
  // additive and intentionally separate from application_submitted so that
  // profile-first applicants (who never submit an application) are also
  // captured, and so the activation funnel's terminal step is unaffected.
  trackApplicantOnboardingCompleted({
    applicantProfileId = null,
    pathType = null,
    exitScreen = null,
    jobId = null,
    userId = null,
    trigger = ANALYTICS_TRIGGERS.ONBOARDING,
    userType = 'candidate'
  } = {}) {
    const identity = this.getCandidateIdentity({
      userId,
      applicantProfileId,
      userType
    })

    this.track('applicant_onboarding_completed', {
      ...identity,
      path_type: pathType,
      exit_screen: exitScreen,
      job_id: jobId ? jobId.toString() : null,
      trigger,
      timestamp: new Date().toISOString()
    })
  },

  // Track feature usage
  trackFeatureUsage(featureName, context = {}) {
    this.track('feature_used', {
      feature_name: featureName,
      ...context,
      timestamp: new Date().toISOString()
    })
  }
}

// Vue plugin to make analytics available in components
export default function (context, inject) {
  inject('analytics', analytics)
}
